import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid2,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import { modernPageShellSx } from "../../../../../styles/modernListingPageSx";
import {
  ALERT_VIEW_LABELS,
  AlertSeverity,
  AlertView,
  isAlertGroupRow,
} from "../../../../../models/Alerts/Alert";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import {
  AlertsTrackExtra,
  trackAlertsAction,
} from "../../../../../analytics/alertsTracking";
import AlertRow from "../../components/alerts/AlertRow";
import AlertStatTiles from "../../components/alerts/AlertStatTiles";
import {
  SectionChips,
  SectionRail,
} from "../../components/alerts/SectionChips";
import {
  alertDetailPath,
  useOpenAlert,
} from "../../components/alerts/useOpenAlert";
import {
  SEVERITY_LABELS,
  getAlertColors,
  getSeverityTone,
} from "../../components/alerts/alertUi";

const VIEWS: AlertView[] = ["INBOX", "UNREAD", "OPEN", "RESOLVED", "ARCHIVED"];
const SEVERITIES: AlertSeverity[] = ["HIGH", "MEDIUM", "LOW"];

const parseView = (value: string | null): AlertView =>
  VIEWS.includes(value as AlertView) ? (value as AlertView) : "INBOX";

const parseGroupId = (value: string | null): number | null => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const AlertsInbox: React.FC = () => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const list = alertsStore.inbox;
  const openAlert = useOpenAlert("inbox", list);
  const [isBulkBusy, setIsBulkBusy] = useState(false);

  const isFirstSync = useRef(true);
  const urlKey = searchParams.toString();

  // The URL (e.g. "View all" from the navbar panel, or a group row's
  // `?group=`) seeds the filters.
  useEffect(() => {
    const view = parseView(searchParams.get("view"));
    const section = searchParams.get("section");
    const groupId = parseGroupId(searchParams.get("group"));
    if (
      isFirstSync.current ||
      view !== list.view ||
      section !== list.section ||
      groupId !== list.groupId
    ) {
      isFirstSync.current = false;
      list.init(view, section, groupId);
    }
    alertsStore.loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, urlKey]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (list.view !== "INBOX") next.set("view", list.view);
    if (list.section) next.set("section", list.section);
    if (list.groupId) next.set("group", String(list.groupId));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.view, list.section, list.groupId]);

  const inGroupView = list.groupId != null;
  const groupTitle = list.items[0]?.title ?? "Grouped alerts";

  const closeGroup = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("group");
    setSearchParams(next);
  };

  const selectedCount = list.selectedIds.length;
  const selectedUnread = list.selectedItems.filter(
    (item) => item.me.read_at == null,
  ).length;
  const isArchivedView = list.view === "ARCHIVED";
  const sectionUnread = list.section
    ? (alertsStore.sections.find((row) => row.code === list.section)?.unread ??
      0)
    : alertsStore.totalUnread;

  const runAction = async (
    action: () => Promise<void>,
    success: string,
    fallback: string,
  ) => {
    setIsBulkBusy(true);
    list.setActionFeedback("");
    try {
      await action();
      list.setActionFeedback("", success);
    } catch (error: any) {
      list.setActionFeedback(error?.message || fallback);
    } finally {
      setIsBulkBusy(false);
    }
  };

  const filterContext = (): AlertsTrackExtra => ({
    view: list.view,
    section: list.section,
    severityFilter: list.severity,
  });

  const handleViewChange = (view: AlertView) => {
    if (view === list.view) return;
    trackAlertsAction("view_change", "inbox", { ...filterContext(), view });
    list.setView(view);
  };

  const handleSectionChange = (section: string | null) => {
    if (section === list.section) return;
    trackAlertsAction("section_change", "inbox", {
      ...filterContext(),
      section,
    });
    list.setSection(section);
  };

  const handleSeverityChange = (severity: AlertSeverity | null) => {
    if (severity === list.severity) return;
    trackAlertsAction("severity_change", "inbox", {
      ...filterContext(),
      severityFilter: severity,
    });
    list.setSeverity(severity);
  };

  const handlePageChange = (page: number) => {
    trackAlertsAction("page_change", "inbox", { ...filterContext(), page });
    list.setPage(page);
  };

  const handleSelectAll = () => {
    trackAlertsAction("select_all", "inbox", {
      ...filterContext(),
      count: list.isAllSelected ? 0 : list.items.length,
    });
    list.toggleSelectAll();
  };

  const handleClearSelection = () => {
    trackAlertsAction("bulk_clear", "inbox", {
      ...filterContext(),
      count: list.selectedIds.length,
    });
    list.clearSelection();
  };

  const handleRetry = () => {
    trackAlertsAction("retry", "inbox", filterContext());
    list.load();
  };

  /** Alert ids behind the selection: a selected group row covers its group. */
  const selectedAlertIds = () =>
    alertsStore.expandRowIds([...list.selectedItems], inGroupView);

  const handleBulkRead = () => {
    trackAlertsAction("bulk_read", "inbox", {
      ...filterContext(),
      count: list.selectedIds.length,
    });
    let count = 0;
    runAction(
      async () => {
        const ids = await selectedAlertIds();
        count = ids.length;
        await alertsStore.markRead(ids);
        list.clearSelection();
      },
      "",
      "Failed to mark read.",
    ).then(() => {
      if (count && !list.actionError) {
        list.setActionFeedback(
          "",
          `Marked ${count} alert${count === 1 ? "" : "s"} as read.`,
        );
      }
    });
  };

  const handleBulkArchive = () => {
    trackAlertsAction(
      isArchivedView ? "bulk_unarchive" : "bulk_archive",
      "inbox",
      { ...filterContext(), count: list.selectedIds.length },
    );
    let count = 0;
    runAction(
      async () => {
        const ids = await selectedAlertIds();
        count = ids.length;
        if (isArchivedView) await alertsStore.unarchive(ids);
        else await alertsStore.archive(ids);
        list.clearSelection();
      },
      "",
      isArchivedView ? "Failed to unarchive." : "Failed to archive.",
    ).then(() => {
      if (count && !list.actionError) {
        list.setActionFeedback(
          "",
          `${isArchivedView ? "Moved" : "Archived"} ${count} alert${
            count === 1 ? "" : "s"
          }${isArchivedView ? " back to the inbox" : ""}.`,
        );
      }
    });
  };

  /** Marks read, archives, or restores every alert of the open group. */
  const handleGroupAction = (action: "read" | "archive") => {
    const groupId = list.groupId;
    if (!groupId) return;
    trackAlertsAction(
      action === "read" ? "bulk_read" : "bulk_archive",
      "inbox",
      {
        ...filterContext(),
        count: list.totalCount,
      },
    );
    runAction(
      async () => {
        const ids = await alertsStore.loadGroupIds(groupId);
        if (action === "read") {
          await alertsStore.markRead(ids);
          list.load();
        } else {
          await alertsStore.archive(ids);
          closeGroup();
        }
      },
      action === "read"
        ? "All alerts in this group marked as read."
        : "All alerts in this group archived.",
      action === "read" ? "Failed to mark read." : "Failed to archive.",
    );
  };

  const handleMarkAllRead = () => {
    trackAlertsAction("mark_all_read", "inbox", filterContext());
    runAction(
      () => alertsStore.markAllRead(list.section),
      list.section
        ? `All ${alertsStore.sectionLabel(list.section)} alerts marked as read.`
        : "All alerts marked as read.",
      "Failed to mark all read.",
    );
  };

  const handleRefresh = () => {
    trackAlertsAction("refresh", "inbox", filterContext());
    list.setActionFeedback("");
    alertsStore.pollNow();
    list.load();
  };

  const pillSx = (active: boolean, fg: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    height: 30,
    px: 1.5,
    cursor: "pointer",
    borderRadius: "999px",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: active ? fg : colors.subtitle,
    bgcolor: active ? `${fg}14` : colors.surface,
    border: `1px solid ${active ? fg : colors.border}`,
    transition: "all 0.15s ease",
    "&:hover": { borderColor: fg, color: fg },
  });

  return (
    <Grid2 container sx={{ ...modernPageShellSx(theme), minHeight: "auto" }}>
      <Grid2 size={12}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <CustomTypography variant="heading3" color={colors.title}>
              Alerts
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.greyTextColor}
              sx={{ mt: 0.5 }}
            >
              Everything across Commander that needs your attention.
            </CustomTypography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.surface,
                  color: colors.subtitle,
                  "&:hover": {
                    color: colors.accent,
                    borderColor: colors.accent,
                  },
                }}
              >
                {list.isLoading ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  <RefreshRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <AppButton
              label={list.section ? "Mark section read" : "Mark all read"}
              variant="transparentPrimary"
              startIcon={<DoneAllOutlinedIcon />}
              onClick={handleMarkAllRead}
              disabled={isBulkBusy || sectionUnread === 0}
            />
          </Box>
        </Box>

        {inGroupView && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              borderRadius: "16px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
            }}
          >
            <AppButton
              label="All alerts"
              variant="transparentPrimary"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={closeGroup}
              customStyles={{ minHeight: 32, height: 32 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant="heading7"
                color={colors.title}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {groupTitle}
              </CustomTypography>
              <CustomTypography variant="caption" color={colors.subtitle}>
                {list.hasLoaded
                  ? `${list.totalCount} alert${list.totalCount === 1 ? "" : "s"} raised together`
                  : "Loading…"}
              </CustomTypography>
            </Box>
            <AppButton
              label="Mark all read"
              variant="transparentPrimary"
              startIcon={<DoneAllOutlinedIcon />}
              onClick={() => handleGroupAction("read")}
              disabled={isBulkBusy}
              customStyles={{ minHeight: 32, height: 32 }}
            />
            <AppButton
              label="Archive all"
              variant="transparentPrimary"
              startIcon={<ArchiveOutlinedIcon />}
              onClick={() => handleGroupAction("archive")}
              disabled={isBulkBusy}
              customStyles={{ minHeight: 32, height: 32 }}
            />
          </Paper>
        )}

        <AlertStatTiles list={list} />

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            boxShadow: `0 8px 24px ${theme.palette.customColors.shadowVeryLight}`,
          }}
        >
          <Grid2 container>
            {/* Section rail */}
            <Grid2
              size={{ xs: 12, md: 3 }}
              sx={{
                display: { xs: "none", md: "block" },
                borderRight: `1px solid ${colors.hairline}`,
                bgcolor: colors.surfaceMuted,
                p: 1.5,
              }}
            >
              <CustomTypography
                variant="heading9"
                color={colors.muted}
                sx={{
                  px: 1.25,
                  pt: 0.5,
                  pb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Sections
              </CustomTypography>
              <SectionRail
                value={list.section}
                onChange={handleSectionChange}
              />
            </Grid2>

            {/* List */}
            <Grid2 size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
              <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 1 }}>
                <Tabs
                  value={list.view}
                  onChange={(_, value: AlertView) => handleViewChange(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={listingStatusTabsStyles(theme)}
                >
                  {VIEWS.map((view) => (
                    <Tab
                      key={view}
                      value={view}
                      label={ALERT_VIEW_LABELS[view]}
                    />
                  ))}
                </Tabs>

                <SectionChips
                  sx={{ display: { xs: "flex", md: "none" }, mt: 1.5 }}
                  value={list.section}
                  onChange={handleSectionChange}
                />

                {/* Severity + select all / bulk bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    py: 1.5,
                  }}
                >
                  {selectedCount > 0 ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: "10px",
                        bgcolor: colors.unreadBg,
                        border: `1px solid ${colors.accent}33`,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={list.isAllSelected}
                        indeterminate={!list.isAllSelected}
                        onChange={handleSelectAll}
                        sx={{
                          p: 0.5,
                          color: colors.accent,
                          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                            color: colors.accent,
                          },
                        }}
                      />
                      <CustomTypography
                        variant="heading8"
                        color={colors.accent}
                      >
                        {selectedCount} selected
                      </CustomTypography>
                      <Box sx={{ flex: 1 }} />
                      {!isArchivedView && (
                        <AppButton
                          label="Mark read"
                          variant="transparentPrimary"
                          startIcon={<DoneOutlinedIcon />}
                          onClick={handleBulkRead}
                          disabled={isBulkBusy || selectedUnread === 0}
                          customStyles={{ minHeight: 32, height: 32 }}
                        />
                      )}
                      <AppButton
                        label={isArchivedView ? "Move to inbox" : "Archive"}
                        variant="transparentPrimary"
                        startIcon={
                          isArchivedView ? (
                            <UnarchiveOutlinedIcon />
                          ) : (
                            <ArchiveOutlinedIcon />
                          )
                        }
                        onClick={handleBulkArchive}
                        disabled={isBulkBusy}
                        customStyles={{ minHeight: 32, height: 32 }}
                      />
                      <Tooltip title="Clear selection">
                        <IconButton
                          size="small"
                          onClick={handleClearSelection}
                          sx={{ color: colors.subtitle, borderRadius: "8px" }}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Tooltip title="Select all on this page">
                          <span>
                            <Checkbox
                              size="small"
                              checked={false}
                              disabled={list.items.length === 0}
                              onChange={handleSelectAll}
                              sx={{ p: 0.5, ml: 0.5, color: colors.muted }}
                            />
                          </span>
                        </Tooltip>
                        <CustomTypography
                          variant="caption"
                          color={colors.subtitle}
                        >
                          {list.hasLoaded
                            ? `${list.totalCount} alert${list.totalCount === 1 ? "" : "s"}`
                            : "Loading…"}
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleSeverityChange(null)}
                          sx={pillSx(list.severity == null, colors.accent)}
                        >
                          All severities
                        </Box>
                        {SEVERITIES.map((severity) => {
                          const tone = getSeverityTone(theme, severity);
                          return (
                            <Box
                              key={severity}
                              component="button"
                              type="button"
                              onClick={() =>
                                handleSeverityChange(
                                  list.severity === severity ? null : severity,
                                )
                              }
                              sx={pillSx(list.severity === severity, tone.fg)}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  bgcolor: tone.fg,
                                }}
                              />
                              {SEVERITY_LABELS[severity]}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Box>

                <InlineFormError message={list.actionError} sx={{ mt: 0 }} />
                {list.actionSuccess && (
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.successGreen}
                    sx={{ mb: 1 }}
                  >
                    {list.actionSuccess}
                  </CustomTypography>
                )}
              </Box>

              <Box
                sx={{
                  borderTop: `1px solid ${colors.hairline}`,
                  minHeight: 320,
                }}
              >
                {list.isLoading && !list.hasLoaded ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", gap: 1.25, px: 2.5, py: 1.75 }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={38}
                        height={38}
                        sx={{ borderRadius: "10px" }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" height={20} />
                        <Skeleton width="85%" height={16} />
                        <Skeleton
                          width={160}
                          height={22}
                          sx={{ borderRadius: "999px" }}
                        />
                      </Box>
                    </Box>
                  ))
                ) : list.error ? (
                  <Box sx={{ p: 2.5 }}>
                    <InlineFormError message={list.error} />
                    <AppButton
                      label="Try again"
                      variant="transparentPrimary"
                      onClick={handleRetry}
                    />
                  </Box>
                ) : list.items.length === 0 ? (
                  <Box
                    sx={{
                      m: 2.5,
                      minHeight: "14rem",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      borderRadius: 2,
                      border: `1px dashed ${colors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: colors.surfaceMuted,
                        color: colors.muted,
                        mb: 1.5,
                      }}
                    >
                      <NotificationsOffOutlinedIcon />
                    </Box>
                    <CustomTypography variant="heading6" color={colors.title}>
                      {list.view === "UNREAD" || list.view === "INBOX"
                        ? "You're all caught up"
                        : `No ${ALERT_VIEW_LABELS[list.view].toLowerCase()} alerts`}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      color={theme.palette.customColors.greyTextColor}
                      sx={{ mt: 0.5 }}
                    >
                      {list.section || list.severity
                        ? "Try a different section or severity."
                        : "New alerts will appear here as they come in."}
                    </CustomTypography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      opacity: list.isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {list.items.map((alert) => (
                      <AlertRow
                        key={alert.id}
                        alert={alert}
                        asGroup={!inGroupView && isAlertGroupRow(alert)}
                        selectable
                        selected={list.selectedIds.includes(alert.id)}
                        onToggleSelect={() => list.toggleSelected(alert.id)}
                        onOpen={() => openAlert(alert)}
                        onOpenDetail={() => {
                          trackAlertsAction("row_detail", "inbox", {
                            ...filterContext(),
                            alert,
                          });
                          navigate(alertDetailPath(alert.id));
                        }}
                        onMarkRead={() => {
                          trackAlertsAction("row_read", "inbox", {
                            ...filterContext(),
                            alert,
                          });
                          runAction(
                            async () =>
                              alertsStore.markRead(
                                await alertsStore.expandRowIds(
                                  [alert],
                                  inGroupView,
                                ),
                              ),
                            "",
                            "Failed to mark read.",
                          );
                        }}
                        onArchive={() => {
                          trackAlertsAction("row_archive", "inbox", {
                            ...filterContext(),
                            alert,
                          });
                          runAction(
                            async () =>
                              alertsStore.archive(
                                await alertsStore.expandRowIds(
                                  [alert],
                                  inGroupView,
                                ),
                              ),
                            "Alert archived.",
                            "Failed to archive.",
                          );
                        }}
                        onUnarchive={() => {
                          trackAlertsAction("row_unarchive", "inbox", {
                            ...filterContext(),
                            alert,
                          });
                          runAction(
                            async () =>
                              alertsStore.unarchive(
                                await alertsStore.expandRowIds(
                                  [alert],
                                  inGroupView,
                                ),
                              ),
                            "Alert moved back to the inbox.",
                            "Failed to unarchive.",
                          );
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {list.totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                    borderTop: `1px solid ${colors.hairline}`,
                  }}
                >
                  <Pagination
                    page={list.pageNo}
                    count={list.totalPages}
                    onChange={(_, page) => handlePageChange(page)}
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontWeight: 600,
                        color: colors.subtitle,
                        borderRadius: "8px",
                      },
                      "& .Mui-selected": {
                        bgcolor: `${colors.accent} !important`,
                        color: `${theme.palette.customColors.white} !important`,
                      },
                    }}
                  />
                </Box>
              )}
            </Grid2>
          </Grid2>
        </Paper>
      </Grid2>
    </Grid2>
  );
};

export default observer(AlertsInbox);
